import React, { useState, useEffect, useRef } from "react";
import { X, Zap, LucideIcon, CircleCheck } from "lucide-react";
import { OutboundCallInterface } from "../OutboundCallinterface";
import aposTropheIcon from "@/assets/invertedcommas.svg";
import { NoAgentNotification } from "@/components/ui/NoAgentNotification";
import { MediaDeviceFailure } from "livekit-client";
import { useCallStatusPolling } from "@/components/hooks/useCallStatusPolling";

import {
  LiveKitRoom,
  RoomAudioRenderer,
} from "@livekit/components-react";

interface LivekitVoiceProps {
  pageCategory: string;
  isDialogOpen: boolean;
  toggleDialog: () => void;
  phoneNumber: string;
  callHints: any[];
  callHintsTable: any[];
  agentName: string;
  features: string[];
  icon: string;
  callTitle: string;
  voiceConfig: {
    baseUrl: string;
    url: string;
    user_id: string;
    agent_id: string;
    overide_previous_context: boolean;
    tenant_id: string;
    workspace_id: string;
  };
  try_speaking: string[] | [];
  activities: string[] | [];
  dynamicVariables: any;
  isFeedbackFormOpen?: boolean;
  ttsConfigId: string | null;
  setIsFeedbackFormOpen?: (isOpen: boolean) => void;
  setConnectionDetails?: (details: any) => void;
}

interface TranscriptMessage {
  id: string;
  speaker: string;
  text: string;
  time: string;
  type?: string;
  timestamp: number;
}

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

interface SuggestionItemProps {
  text: string;
  icon?: string;
}

interface HintItemProps {
  text: string;
}

interface FeatureItemProps {
  text: string;
  enabled?: boolean;
}

interface ActivityItemProps {
  text: string;
  icon: LucideIcon;
  status?: "active" | "completed" | "warning";
}

interface TranscriptMessageProps {
  speaker: string;
  text: string;
}

// Component implementations with proper typing
const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  children,
  className = ""
}) => (
  <div className={`${className}`}>
    <h2 className="text-xl font-normal text-gray-400 mb-8 text-left font-inter">
      {title}
    </h2>
    {children}
  </div>
);

const SuggestionItem: React.FC<SuggestionItemProps> = ({
  text,
  icon = '"'
}) => (
  <div className="flex items-start space-x-2">
    <img src={aposTropheIcon} className="w-4 h-4" alt="apostrophe icon" />
    <p className="text-gray-300 text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const HintItem: React.FC<HintItemProps> = ({ text }) => (
  <div className="flex items-center space-x-3">
    <div className="w-2 h-2 bg-white rounded-full"></div>
    <p className="text-gray-300 text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const FeatureItem: React.FC<FeatureItemProps> = ({ text, enabled = true }) => (
  <div className="flex items-center space-x-3">
    <CircleCheck
      size={16}
      className={enabled ? "text-green-400" : "text-gray-500"}
    />
    <p
      className={`text-base md:text-xl font-manrope ${enabled ? "text-gray-300" : "text-gray-500"
        }`}
    >
      {text}
    </p>
  </div>
);

const ActivityItem: React.FC<ActivityItemProps> = ({
  text,
  icon: Icon,
  status = "active"
}) => (
  <div className="flex items-center space-x-4">
    <Icon size={16} className="text-gray-400" />
    <p className="text-gray-300 text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const TranscriptMessageComponent: React.FC<TranscriptMessageProps> = ({
  speaker,
  text
}) => (
  <div
    className={`mb-2 ${speaker === "user" ? "ml-auto" : "mr-auto"} max-w-[80%]`}
  >
    <div className={`px-4 py-1`}>
      <div className="flex items-center justify-between mb-1">
        <p
          className={` text-base sm:text-xl leading-relaxed font-manrope ${speaker === "user"
            ? "text-gray-500 text-right ml-auto"
            : "text-white text-left mr-auto"
            }`}
        >
          {text}
        </p>
      </div>
    </div>
  </div>
);

const OutboundCallDashboard: React.FC<LivekitVoiceProps> = ({
  isDialogOpen,
  toggleDialog,
  phoneNumber,
  callHints,
  callHintsTable,
  callTitle,
  agentName,
  ttsConfigId,
  voiceConfig,
  icon,
  features,
  dynamicVariables,
  isFeedbackFormOpen,
  setIsFeedbackFormOpen,
  setConnectionDetails,
  try_speaking,
  activities
}) => {
  type ConnectionDetails = {
    serverUrl: string;
    roomName: string;
    participantName: string;
    participantToken: string;
    voiceConfig: {
      tokenUrl: string;
    };
    ttsConfigId: string;
  };

  const [connectionDetails, updateConnectionDetails] = useState<
    ConnectionDetails | undefined
  >(undefined);
  const [agentState, setAgentState] = useState<any>("disconnected"); // Changed from AgentState to any

  const [transcriptMessages, setTranscriptMessages] = useState<
    TranscriptMessage[]
  >([]);

  const [activeTab, setActiveTab] = useState("transcript");
  const [isCalling, setIsCalling] = useState(false);
  const [callEnded, setCallEnded] = useState<boolean>(false);
  const [currentCallId, setCurrentCallId] = useState<string | undefined>();
  const [shouldPollStatus, setShouldPollStatus] = useState(false);
  const transcriptDesktopRef = useRef<HTMLDivElement>(null);
  const transcriptMobileRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const [seconds, setSeconds] = useState(0);

  // Poll for call status when serverUrl is empty
  const { data: polledCallDetails } = useCallStatusPolling({
    baseUrl: voiceConfig.baseUrl,
    callId: currentCallId || "",
    userId: voiceConfig.user_id.toString(),
    enabled: shouldPollStatus && !!currentCallId
  });

  const tabs = [
    { id: "transcript", label: "Transcript" },
    { id: "try_speaking", label: "Try Speaking" },
    { id: "call_hints", label: "Call Hints" },
    { id: "features", label: "Features" },
    { id: "activities", label: "Activities" }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "transcript":
        return (
          <div className="rounded-lg h-[30vh] overflow-y-auto space-y-2 scroll-smooth">
            {transcriptMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="w-12 h-12 mb-3 rounded-full bg-gray-800/50 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <h4 className="text-base font-medium text-gray-500 mb-1 text-center">
                  Your conversation will appear here
                </h4>
              </div>
            ) : (
              <div className="space-y-3">
                {transcriptMessages.map((message) => (
                  <TranscriptMessageComponent
                    key={message.id}
                    speaker={message.speaker}
                    text={message.text}
                  />
                ))}
              </div>
            )}
            <div ref={transcriptMobileRef} />
          </div>
        );

      case "try_speaking":
        return (
          <div className="space-y-3">
            {try_speaking.map((suggestion, index) => (
              <SuggestionItem key={index} text={suggestion} />
            ))}
          </div>
        );

      case "call_hints":
        return (
          <div className="space-y-3">
            {callHints.map((hint: any, index: number) => (
              <HintItem key={index} text={hint} />
            ))}
          </div>
        );

      case "activities":
        return (
          <div className="space-y-6">
            {activities.map((activity, index) => (
              <ActivityItem
                key={index}
                text={activity}
                icon={Zap}
                status="active"
              />
            ))}
          </div>
        );

      case "features":
        return (
          <div className="space-y-3">
            {features.map((feature, index) => (
              <FeatureItem key={index} text={feature} />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setSeconds(0);
    timerRef.current = window.setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const addTranscriptMessage = (
    message: Omit<TranscriptMessage, "id" | "timestamp">
  ) => {
    const newMessage: TranscriptMessage = {
      ...message,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: Date.now()
    };

    setTranscriptMessages((prev) => {
      const updated = [...prev, newMessage];
      return updated;
    });
  };

  useEffect(() => {
    if (isDialogOpen) {
      setTranscriptMessages([]);
    }
    return () => {
      updateConnectionDetails(undefined);
      setTranscriptMessages([]);
      setShouldPollStatus(false);
      setCurrentCallId(undefined);
    };
  }, [isDialogOpen]);

  // Handle polled connection details
  useEffect(() => {
    if (polledCallDetails && polledCallDetails.serverUrl) {
      console.log("Polled connection details received:", polledCallDetails);
      // Stop polling
      setShouldPollStatus(false);
      setIsCalling(true);
      // Update connection details
      updateConnectionDetails({
        ...polledCallDetails,
        metadata: {
          ttsConfigId: ttsConfigId,
          phoneNumber: phoneNumber
        }
      } as any);
      startTimer();
    }
  }, [polledCallDetails, ttsConfigId, phoneNumber]);

  useEffect(() => {
    if (transcriptDesktopRef.current) {
      transcriptDesktopRef.current.scrollIntoView({ behavior: "smooth" });
    }

    if (transcriptMobileRef.current) {
      transcriptMobileRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcriptMessages]);

  const handleEndCall = () => { };

  return (
    <div className="min-h-screen bg-black text-white font-manrope">
      {/* Show loading overlay when polling */}

      {/* Desktop Layout - Hidden on Mobile */}
      {/* @ts-ignore */}
      <LiveKitRoom
        token={connectionDetails?.participantToken}
        serverUrl={connectionDetails?.serverUrl}
        connect={connectionDetails !== undefined}
        audio={true}
        video={false}
        onMediaDeviceFailure={onDeviceFailure}
        onDisconnected={() => {
          updateConnectionDetails(undefined);
          setShouldPollStatus(false);
          setCurrentCallId(undefined);
        }}
        className="grid grid-rows-[2fr_1fr] items-center"
      >
        <div className="hidden md:flex">
          {/* Left Sidebar */}
          <div className="flex-1 flex flex-col items-center pl-20">
            <div className="flex w-full justify-start mt-10 ">
              <button
                className="w-14 h-14 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
                onClick={() => {
                  toggleDialog();
                }}
              >
                <X size={30} className="text-gray-300" />
              </button>
            </div>

            <div className="flex flex-col mt-40 space-y-12">
              {try_speaking && try_speaking.length > 0 && (
                <SidebarSection title="Try Speaking">
                  <div className="space-y-3">
                    {try_speaking.map((suggestion, index) => (
                      <SuggestionItem key={index} text={suggestion} />
                    ))}
                  </div>
                </SidebarSection>
              )}

              {callHints && callHints.length > 0 && (
                <SidebarSection title="Call Hints">
                  <div className="space-y-3">
                    {callHints.map((hint: any, index: number) => (
                      <HintItem key={index} text={hint} />
                    ))}
                  </div>
                </SidebarSection>
              )}

              {features && features.length > 0 && (
                <SidebarSection title="Features">
                  <div className="space-y-3">
                    {features.map((feature, index) => (
                      <FeatureItem key={index} text={feature} />
                    ))}
                  </div>
                </SidebarSection>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-start mt-12 space-y-16 pr-8">
            <div className="text-center">
              <h1 className="text-3xl font-normal text-gray-300 font-inter">
                {callTitle}
              </h1>
            </div>

            <VoiceAssistantAnimation
              isCalling={isCalling}
              setIsCalling={setIsCalling}
              onStateChange={setAgentState}
              agentName={agentName}
              disconnectCall={() => {
                // setIsFeedbackFormOpen(true);
                toggleDialog();
              }}
              icon={icon}
              onTranscriptMessage={addTranscriptMessage}
              startTimer={startTimer}
              stopTimer={stopTimer}
              seconds={seconds}
              callEnded={callEnded}
              setCallEnded={setCallEnded}
              voiceConfig={voiceConfig}
            />

            <RoomAudioRenderer />
            <NoAgentNotification state={agentState} />
          </div>

          {/* Right Sidebar */}
          <div className="flex-1 p-6 justify-start items-center mt-28">
            <div className="flex flex-col space-y-8 pr-24">
              <SidebarSection title="Transcript" className="flex-1">
                <div className="flex flex-col rounded-lg p-4 h-96 overflow-y-auto space-y-2 scroll-smooth">
                  {transcriptMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <div className="w-16 h-16 mb-4 rounded-full bg-gray-800/50 flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                      </div>
                      <h4 className="text-xl font-medium text-gray-500 mb-2">
                        Your conversations with {agentName} will appear here
                      </h4>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transcriptMessages.map((message) => (
                        <TranscriptMessageComponent
                          key={message.id}
                          speaker={message.speaker}
                          text={message.text}
                        />
                      ))}
                    </div>
                  )}
                  <div ref={transcriptDesktopRef} />
                </div>
              </SidebarSection>

              {activities && activities.length > 0 && (
                <SidebarSection title="Activities">
                  <div className="space-y-6">
                    {activities.map((activity, index) => (
                      <ActivityItem
                        key={index}
                        text={activity}
                        icon={Zap}
                        status="active"
                      />
                    ))}
                  </div>
                </SidebarSection>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Layout - Visible only on Mobile */}
        <div className="md:hidden flex flex-col min-h-screen">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center space-x-4">
              <button
                className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
                onClick={() => {
                  handleEndCall();
                  if (isCalling || callEnded) toggleDialog();
                }}
              >
                <X size={20} className="text-gray-300" />
              </button>
              <h1 className="text-xl font-normal text-gray-300 font-inter">
                {callTitle}
              </h1>
            </div>
          </div>

          <div className="flex items-center justify-center px-4">
            <VoiceAssistantAnimation
              isCalling={isCalling}
              setIsCalling={setIsCalling}
              onStateChange={setAgentState}
              agentName={agentName}
              disconnectCall={() => {
                toggleDialog();
              }}
              icon={icon}
              onTranscriptMessage={() => { }}
              startTimer={startTimer}
              stopTimer={stopTimer}
              seconds={seconds}
              callEnded={callEnded}
              setCallEnded={setCallEnded}
              voiceConfig={voiceConfig}
            />
            <NoAgentNotification state={agentState} />
          </div>

          {/* Tab Navigation */}
          <div>
            {/* Tab Headers */}
            <div className="flex-shrink-0 px-12">
              <div className="flex overflow-x-auto scrollbar-hide">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id
                      ? "text-white"
                      : "text-gray-400 hover:text-gray-300"
                      }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4 w-[100vw] overflow-y-auto">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </LiveKitRoom>
    </div>
  );
};

interface VoiceAssistantAnimationProps {
  isCalling: boolean;
  setIsCalling: (val: boolean) => void;
  onStateChange: (state: any) => void;
  agentName: string;
  disconnectCall: () => void;
  icon: string;
  onTranscriptMessage: (message: Omit<TranscriptMessage, "id" | "timestamp">) => void;
  startTimer: () => void;
  stopTimer: () => void;
  seconds: number;
  callEnded: boolean;
  setCallEnded: (val: boolean) => void;
  voiceConfig: {
    baseUrl: string;
    url: string;
    user_id: string;
    agent_id: string;
    overide_previous_context: boolean;
    tenant_id: string;
    workspace_id: string;
  };
}

function VoiceAssistantAnimation({
  isCalling,
  setIsCalling,
  onStateChange,
  agentName,
  disconnectCall,
  icon,
  onTranscriptMessage,
  startTimer,
  stopTimer,
  seconds,
  callEnded,
  setCallEnded,
  voiceConfig
}: VoiceAssistantAnimationProps): JSX.Element {
  return (
    <>
      <OutboundCallInterface
        agentName={agentName}
        icon={icon}
        voiceConfig={voiceConfig}
      />
    </>
  );
}

function onDeviceFailure(error?: MediaDeviceFailure) {
  console.error(error);
  alert(
    "Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab"
  );
}

export default OutboundCallDashboard;
